// iter586 — NIS Gauge: експозира Nutramilo Insulin Score (0–100%) на Dashboard.
// Числото се изчислява backend-side в /api/meals/today (nis_by_meal + nis_daily,
// services/nis_adapter.py → nutramilo-nis SDK). Тук само го визуализираме.
import React from "react";
import { Link } from "react-router-dom";
import { nisMeaning } from "@/utils/plainMeaning";  // iter1081

// Радиален gauge (270° дъга). Чист пръстен — текстът се наслагва от родителя.
export function NisRadialGauge({ percent = 0, color = "#10B981", size = 84, stroke = 9, isDark = false, empty = false }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const arc = c * 0.75; // 270°
  const val = Math.max(0, Math.min(100, Number(percent) || 0));
  const filled = arc * (val / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(135deg)" }} aria-hidden="true">
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB"}
        strokeWidth={stroke} strokeDasharray={`${arc} ${c}`} strokeLinecap="round"
      />
      {!empty && (
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={`${filled} ${c}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      )}
    </svg>
  );
}

const MEAL_BG = { breakfast: "закуска", lunch: "обяд", dinner: "вечеря", snack: "междинно хранене" };
const MEAL_EN = { breakfast: "breakfast", lunch: "lunch", dinner: "dinner", snack: "snack" };

export default function NisGaugeCard({ mealDilData, isDark, bg }) {
  const nis = mealDilData?.last_meal_nis;
  const daily = mealDilData?.nis_daily;
  const mealType = mealDilData?.last_meal_type;
  const has = nis && typeof nis.percent === "number";
  const pct = has ? Math.round(nis.percent) : 0;
  const color = has ? (nis.color || "#10B981") : (isDark ? "#6B7280" : "#9CA3AF");
  const tier = has ? (bg ? nis.tier_bg : nis.tier_en) : null;
  const mealLabel = mealType ? (bg ? MEAL_BG[mealType] : MEAL_EN[mealType]) : null;

  return (
    <div
      className={`relative rounded-2xl border transition-all
        ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5 hover:border-sky-500/30" : "bg-white border-gray-200 hover:border-sky-200"}`}
      data-testid="cc-nis-gauge"
    >
      <Link to="/dashboard/diary" className="block p-5 group">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl" aria-hidden="true">⚡</span>
          <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Инсулинов отговор — последно хранене" : "Insulin response — last meal"}
          </h3>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative shrink-0" style={{ width: 84, height: 84 }}>
            <NisRadialGauge percent={pct} color={color} size={84} stroke={9} isDark={isDark} empty={!has} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-extrabold tabular-nums" style={{ color: has ? color : undefined }} data-testid="cc-nis-gauge-value">
                {has ? `${pct}%` : "—"}
              </span>
            </div>
          </div>
          <div className="min-w-0">
            {has ? (
              <>
                <div className="text-sm font-bold" style={{ color }}>{tier}</div>
                {bg && nisMeaning(pct) && (
                  <div className={`text-[11px] mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}
                       data-testid="cc-nis-gauge-plain">
                    {nisMeaning(pct)}
                  </div>
                )}
                {mealLabel && (
                  <div className={`text-xs mt-0.5 capitalize ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {mealLabel}
                  </div>
                )}
                {mealDilData?.last_meal_name && (
                  <div className={`text-[11px] mt-0.5 truncate max-w-[140px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {mealDilData.last_meal_name}
                  </div>
                )}
              </>
            ) : (
              <p className={`text-xs leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`} data-testid="cc-nis-gauge-empty">
                {bg
                  ? "Запишете хранене и веднага ще видите колко силно то стимулира инсулина Ви (0–100%)."
                  : "Log a meal to instantly see how strongly it stimulates your insulin (0–100%)."}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs">
          {daily?.meal_count ? (
            <span className={isDark ? "text-gray-400" : "text-gray-500"} data-testid="cc-nis-gauge-daily">
              {bg
                ? <>Пик днес: <strong style={{ color: daily.peak_color }}>{Math.round(daily.peak_percent)}%</strong> · средно {Math.round(daily.avg_percent)}%</>
                : <>Today's peak: <strong style={{ color: daily.peak_color }}>{Math.round(daily.peak_percent)}%</strong> · avg {Math.round(daily.avg_percent)}%</>}
            </span>
          ) : (
            <span className={isDark ? "text-gray-500" : "text-gray-400"}>
              {bg ? "0–100% — колко инсулин изисква едно хранене" : "0–100% — how much insulin one meal demands"}
            </span>
          )}
          <span className={`inline-flex items-center gap-1 font-semibold ${isDark ? "text-sky-300" : "text-sky-700"} group-hover:translate-x-0.5 transition-transform`}>
            ➜ {bg ? "Дневник" : "Diary"}
          </span>
        </div>
      </Link>
    </div>
  );
}
